import dayjs from "dayjs";
import moment, { Moment, MomentBuiltinFormat } from "moment";
import "moment/locale/cs";
import "moment/locale/de";
import "moment/locale/en-gb";
import "moment/locale/hu";
import "moment/locale/pl";
import "moment/locale/sk";

import { getEnvironment, getLanguageFromLocalStorage } from "utils";

import { ADMIN_VERIFICATION_DEADLINE, LOCALE_DICT } from "consts";

import { ACCOUNTING_METHODOLOGY, CROP_TYPE_FCPO, CarbonProgram } from "models";

export const formatDate = (date: string, format: string, inputFormat?: string | MomentBuiltinFormat) => {
  const locale = getLanguageFromLocalStorage({ forDates: true });

  return moment(date, inputFormat || "MM-DD-YYYY")
    .locale(LOCALE_DICT[locale] ? LOCALE_DICT[locale] : LOCALE_DICT.de)
    .format(format || "MM-DD-YYYY");
};

// longMonth uses November instead of Nov
// trimDot can be used to remove the trailing dot (e.g. in german short month)
export const formatDayMonth = (date: string, { longMonth = false, trimDot = false } = {}) => {
  const language = getLanguageFromLocalStorage({ forDates: true });

  const dateString = moment(date)
    .locale(language)
    .format(language?.toLowerCase() === "en" ? `${longMonth ? "MMMM" : "MMM"} Do` : `Do ${longMonth ? "MMMM" : "MMM"}`);

  if (trimDot && dateString.endsWith(".")) {
    return dateString.slice(0, -1);
  }

  return dateString;
};

export const formatDayDate = (date: string, { ordinal = false } = {}) => {
  const language = getLanguageFromLocalStorage({ forDates: true });

  return moment(date, "YYYY-MM-DD HH:mm Z")
    .locale(language)
    .format(!ordinal ? "DD MMM YYYY" : language?.toLowerCase() === "en" ? "MMM Do YYYY" : "Do MMM YYYY");
};

export const getAdminVerificationYearlyDataYear = (): number => {
  const cutoffDate = moment(`${moment().year()}-${ADMIN_VERIFICATION_DEADLINE}`).dayOfYear();

  return moment().dayOfYear() < cutoffDate ? moment().year() - 1 : moment().year();
};

// This function checks if the current date has passed the ADMIN_VERIFICATION_DEADLINE for the current year.
// If it has, it returns next year's deadline date. If not, it returns this year's deadline date.
// 15Nov2024 > 31May2025 -> 2025 Deadline
// 1Jun2025 > 31May2025 -> 2026 Deadline
export const getAdminVerificationDeadline = () =>
  moment().dayOfYear() > moment(`${moment().year()}-${ADMIN_VERIFICATION_DEADLINE}`).dayOfYear()
    ? moment(`${moment().year() + 1}-${ADMIN_VERIFICATION_DEADLINE}`)
    : moment(`${moment().year()}-${ADMIN_VERIFICATION_DEADLINE}`);

export const getCurrentBauYears = (currentYear: number) =>
  Array(3)
    .fill(null)
    .map((_, index) => currentYear - (index + 1));

export const getDatePickerDisabledRange = ({ current, startDate, endDate }: { current: moment.Moment; startDate: string; endDate: string }): boolean =>
  current.isBefore(moment(`${startDate}`), "day") || current.isAfter(moment(`${endDate}`), "day");

const calculateOverlap = (start1: Moment, end1: Moment, start2: Moment, end2: Moment) => moment.min(end1, end2).diff(moment.max(start1, start2), "days") + 1;

export const doRangesOverlap = (start1: Moment, end1: Moment, start2: Moment, end2: Moment, maximumPermissibleOverlapInDays?: number) => {
  const overlap = calculateOverlap(start1, end1, start2, end2);

  // returns a negative number if the timeframes are not overlapping, and don't "touch"
  // that number is the gap between the two timeframes (in days), which could be useful in the future, so I won't scrub them with Math.max
  return start1.isSameOrBefore(end2) && start2.isSameOrBefore(end1) && (maximumPermissibleOverlapInDays ? overlap > maximumPermissibleOverlapInDays : true);
};

/**
 * Returns whether a time is withing the reporting period of a given carbon program.
 * This function was mostly copied over from the backend.
 *
 * This function parses the reporting period defined in the carbon program, computes the start and end dates,
 * and determines if the reporting season spans across years.
 *
 * @param {number} time - A timestamp to check.
 * @param {number} reportingYear - The reporting year to check.
 * @param {CarbonProgram} program - The carbon program object containing a reportingPeriod property in "MM/DD" format.
 * @returns {boolean}
 */

export function timeIsWithinReportingPeriod(time: number, reportingYear: number, program?: CarbonProgram | null): boolean {
  const dateToCheck = moment(time);
  const { startDate, endDate } = parseReportingPeriod(reportingYear, program);

  return moment(dateToCheck).isBetween(startDate, endDate, "day", "[]");
}

export const getVerticalLineClassNamesForTime =
  (highlightReportingYear: boolean, reportingYear: number, carbonProgram?: CarbonProgram | null) =>
  (time: number): string[] => {
    if (!highlightReportingYear) return [];
    return timeIsWithinReportingPeriod(time, reportingYear, carbonProgram) ? ["current-year"] : [];
  };

/**
 * Parses the reporting period of a given carbon program and returns relevant date information.
 *
 * @param {CarbonProgram | null | undefined} program - The carbon program object containing a reportingPeriod property.
 * @param {Moment.Moment} referenceDate - The reference date to base calculations on. Defaults to the current date.
 * @returns {Object} An object containing information about the reporting period, including whether it spans across years.
 * @returns {number} year - The current year of the reporting period.
 * @returns {Moment.Moment} startDate - The start date of the reporting period.
 * @returns {Moment.Moment} endDate - The end date of the reporting period.
 * @returns {number} startMonth - The month of the start date.
 * @returns {number} startDay - The day of the start date.
 * @returns {number} endMonth - The month of the end date.
 * @returns {number} endDay - The day of the end date.
 */
function parseReportingPeriod(year: number, program?: CarbonProgram | null) {
  const start = program?.fieldsManagement.reportingPeriod?.start ?? "01/01";
  const end = program?.fieldsManagement.reportingPeriod?.end ?? "12/31";

  // Parse season boundaries
  const [startMonth, startDay] = start.split("/").map(Number);
  const [endMonth, endDay] = end.split("/").map(Number);

  // Convert to zero-based months
  const startMonthZeroIdx = startMonth - 1;
  const endMonthZeroIdx = endMonth - 1;

  // Determine if season crosses year boundary
  const isWrappingSeason = startMonthZeroIdx > endMonthZeroIdx;

  // Adjusted date range
  const startYear = year - (isWrappingSeason ? 1 : 0);
  const startDate = moment([startYear, startMonthZeroIdx, startDay]).startOf("day");
  const endDate = moment([year, endMonthZeroIdx, endDay]).endOf("day");

  return {
    year,
    startDate,
    endDate,
    startMonth,
    startDay,
    endMonth,
    endDay,
  };
}

/**
 * Retrieves the current reporting values based on a carbon program's reporting period.
 * This function parses the reporting period from the provided carbon program and returns
 * relevant date information along with a utility to calculate date ranges for specific years.
 *
 * @param {CarbonProgram | null | undefined} program - The carbon program object containing reporting period information.
 * @returns {Object} An object containing the current reporting year, start and end dates of the reporting period,
 * and a function to get the date range for a specific reporting year.
 * @returns {number} year - The current reporting year based on the program's reporting period.
 * @returns {Date} startDate - The start date of the current reporting period.
 * @returns {Date} endDate - The end date of the current reporting period.
 * @returns {Function} getReportingYearRange - A function that calculates the start and end dates for a given reporting year.
 */
export function getCurrentReportingValues(activeFarmReportingYear: number, program?: CarbonProgram | null) {
  const { startDate, endDate } = parseReportingPeriod(activeFarmReportingYear, program);

  /**
   * Calculates the start and end dates for a specific reporting year based on the program's reporting period.
   *
   * @param {number} year - The year for which to calculate the reporting date range.
   * @returns {{ start: Moment, end: Moment }} An object containing the start and end dates as Moment objects for the specified year.
   */
  function getReportingYearRange(year: number): { start: Moment; end: Moment } {
    const { startDate, endDate } = parseReportingPeriod(year, program);

    return { start: startDate, end: endDate };
  }

  return {
    activeFarmReportingYear,
    startDate,
    endDate,
    getReportingYearRange,
  };
}

type IsCropInCurrentReportingYearParams = {
  carbonProgram?: CarbonProgram | null;
  cropType: CROP_TYPE_FCPO;
  harvestDate: string | null;
  reportingYear: number | undefined;
  seedingDate: string | null;
};

export const cropIsWithinReportingYear = ({ carbonProgram, cropType, harvestDate, reportingYear, seedingDate }: IsCropInCurrentReportingYearParams) => {
  if (!carbonProgram || !harvestDate || !seedingDate || !reportingYear) return false;

  if (carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) {
    return timeIsWithinReportingPeriod(moment(harvestDate).valueOf(), reportingYear, carbonProgram);
  }

  const referenceDate = cropType === CROP_TYPE_FCPO.UNDERSOWING ? seedingDate : harvestDate;
  return timeIsWithinReportingPeriod(moment(referenceDate).valueOf(), reportingYear, carbonProgram);
};

type GetCropReportingYearParams = {
  carbonProgram?: CarbonProgram | null;
  cropType: CROP_TYPE_FCPO;
  harvestEndDate: string | null;
  seedingStartDate: string | null;
};

export const getReportingYearOfDate = (date: Moment, carbonProgram?: CarbonProgram | null): number => {
  const calendarYear = date.year();
  const { startDate, endDate } = parseReportingPeriod(calendarYear, carbonProgram);

  if (date.isBefore(startDate, "day")) return calendarYear - 1;
  if (date.isAfter(endDate, "day")) return calendarYear + 1;
  return calendarYear;
};

// Mirrors the API's Dates.getFieldCropEntryTargetYear: main crops count by harvest date, side crops by seeding date
export const getCropReportingYear = ({ carbonProgram, cropType, harvestEndDate, seedingStartDate }: GetCropReportingYearParams): number | null => {
  const referenceDate = moment(cropType === CROP_TYPE_FCPO.MAIN ? harvestEndDate : seedingStartDate);
  if (!referenceDate.isValid()) return null;

  return getReportingYearOfDate(referenceDate, carbonProgram);
};

export const formatLocalizedMonth = (monthIndex: number, format: "MMM" | "MMMM" = "MMM"): string => {
  const language = getLanguageFromLocalStorage({ forDates: true });
  const locale = LOCALE_DICT[language] ? LOCALE_DICT[language] : LOCALE_DICT.de;

  return moment().month(monthIndex).locale(locale).format(format);
};

export const getLocalizedDateFormat = (): string => {
  // Handle weird behaviour for admin where locale is not set correctly
  if (getEnvironment().admin) {
    return "DD.MM.YYYY";
  }

  const language = getLanguageFromLocalStorage({ forDates: true });
  const locale = LOCALE_DICT[language] || LOCALE_DICT.de;

  dayjs.locale(locale);

  return "L"; // (Short localized date) This automatically provides DD/MM/YYYY, MM/DD/YYYY, DD.MM.YYYY based on locale
};

export const getMiddayMomentDate = (date: moment.Moment): moment.Moment | null => (moment.isMoment(date) ? moment(date).startOf("day").hour(12) : null);

export const getIsSubmissionPeriodOpen = (submissionStartDate: string | null | undefined, submissionEndDate: string | null | undefined): boolean => {
  if (!submissionStartDate || !moment(submissionStartDate).isValid() || !submissionEndDate || !moment(submissionEndDate).isValid()) return false;
  return moment().isBetween(moment(submissionStartDate), moment(submissionEndDate), "day", "[]");
};
